/**
 * 济南市数科职业中等专业学校 - 路由配置文件
 * 
 * 路由结构说明：
 * - 使用 Vue Router 3.x 版本
 * - 采用懒加载方式加载组件，提升首屏加载速度
 * - 路由按模块分组管理，便于维护和扩展
 * - 二级导航采用动态路由参数切换，通过tab参数控制显示内容
 * 
 * 模块划分：
 * 1. 首页模块 (/index)
 * 2. 学校概况模块 (/school)
 * 3. 机构设置模块 (/organization)
 * 4. 专业设置模块 (/majors)
 * 5. 办学优势模块 (/advantages)
 * 6. 教学教研模块 (/teaching)
 * 7. 招生就业模块 (/admission)
 * 8. 合作交流模块 (/cooperation)
 */

import Vue from 'vue'
import Router from 'vue-router'

// ==================== 首页模块 ====================
// 首页 - 网站主入口页面
const Index = function(resolve) { require(['@/views/index/index'], resolve) }

// ==================== 学校概况模块 ====================
// 学校概况统一页面 - 包含学校简介、办学特色、学校荣誉、技能大赛、校园风光、企业办学
const SchoolOverview = function(resolve) { require(['@/views/school/SchoolOverview'], resolve) }

// ==================== 机构设置模块 ====================
// 机构设置统一页面 - 包含学校办公室、招生就业部、学生工作部、教学部、后勤部
const OrganizationOverview = function(resolve) { require(['@/views/organization/OrganizationOverview'], resolve) }

// ==================== 专业设置模块 ====================
// 专业设置统一页面 - 包含机电技术应用、新能源汽车、直播电商、计算机网络、医学检验等8个专业
const MajorsOverview = function(resolve) { require(['@/views/majors/MajorsOverview'], resolve) }

// ==================== 办学优势模块 ====================
// 办学优势统一页面 - 包含团委、资助中心、学生管理规定
const AdvantagesOverview = function(resolve) { require(['@/views/advantages/AdvantagesOverview'], resolve) }
// 办学优势详情页面 - 展示数字化教学、师资力量、素质教育、历年高考成绩详细内容
const AdvantagesDetail = function(resolve) { require(['@/views/index/AdvantagesDetail'], resolve) }

// ==================== 教学教研模块 ====================
// 教学教研统一页面 - 包含教学管理、实训中心、教研教改、教育质量年度报告
const TeachingOverview = function(resolve) { require(['@/views/teaching/TeachingOverview'], resolve) }

// ==================== 招生就业模块 ====================
// 招生就业统一页面 - 包含招生简章、就业信息、毕业生风采
const AdmissionOverview = function(resolve) { require(['@/views/admission/AdmissionOverview'], resolve) }

// ==================== 合作交流模块 ====================
// 合作交流统一页面 - 包含合作交流概述、校企合作、校校合作、社会服务等6个板块
const CooperationOverview = function(resolve) { require(['@/views/cooperation/CooperationOverview'], resolve) }

// ==================== 新闻模块 ====================
// 新闻列表 - 校园动态列表页
const NewsList = function(resolve) { require(['@/views/news/NewsList'], resolve) }
// 新闻详情 - 新闻详情页
const NewsDetails = function(resolve) { require(['@/views/news/NewsDetails'], resolve) }

// ==================== 公告模块 ====================
// 公告详情 - 通知公告详情页
const NoticeDetails = function(resolve) { require(['@/views/notice/NoticeDetails'], resolve) }

// 注册 Vue Router 插件
Vue.use(Router)

/**
 * 创建路由实例
 * 
 * 路由配置说明：
 * - path: URL路径（以 / 开头）
 * - name: 路由名称（用于编程式导航和路由匹配）
 * - component: 对应加载的组件（懒加载方式）
 * - props: 向组件传递路由参数
 * 
 * 使用示例：
 * 1. 模板中使用：<router-link to="/school/intro">学校简介</router-link>
 * 2. 编程式导航：this.$router.push('/school/intro') 或 this.$router.push({ name: 'SchoolOverview' })
 */
const router = new Router({
  routes: [
    // ==================== 首页 ====================
    {
      path: '/',                    // 访问路径：http://localhost:8080/
      name: 'Index',                // 路由名称
      component: Index              // 对应组件
    },

    // ==================== 旧页面重定向（兼容旧版HTML链接）====================
    // 学校介绍：xxgk.html -> /school/intro
    

    // ==================== 学校概况 ====================
    // 学校概况统一页面，通过tab参数切换不同内容
    {
      path: '/school/:tab',
      name: 'SchoolOverview',
      component: SchoolOverview,
      props: function(route) {
        return { activeTab: route.params.tab }
      }
    },
    // 学校概况默认重定向到学校简介
    {
      path: '/school',
      redirect: '/school/intro'
    },


    // ==================== 机构设置 ====================
    // 机构设置统一页面，通过tab参数切换不同部门
    {
      path: '/organization/:tab',
      name: 'OrganizationOverview',
      component: OrganizationOverview,
      props: function(route) {
        return { activeTab: route.params.tab }
      }
    },
    // 机构设置默认重定向到学校办公室
    {
      path: '/organization',
      redirect: '/organization/office'
    },

    // ==================== 专业设置 ====================
    // 专业设置统一页面，通过tab参数切换不同专业
    {
      path: '/majors/:tab',
      name: 'MajorsOverview',
      component: MajorsOverview,
      props: function(route) {
        return { activeTab: route.params.tab }
      }
    },
    // 专业设置默认重定向到机电技术应用
    {
      path: '/majors',
      redirect: '/majors/mechatronics'
    },

    // ==================== 办学优势 ====================
    // 办学优势统一页面，通过tab参数切换不同内容
    {
      path: '/advantages/:tab',
      name: 'AdvantagesOverview',
      component: AdvantagesOverview,
      props: function(route) {
        return { activeTab: route.params.tab }
      }
    },
    // 办学优势默认重定向到团委
    {
      path: '/advantages',
      redirect: '/advantages/youthleague'
    },
    // 办学优势详情页面
    {
      path: '/advantages-detail',
      name: 'AdvantagesDetail',
      component: AdvantagesDetail
    },

    // ==================== 教学教研 ====================
    // 教学教研统一页面，通过tab参数切换不同内容
    {
      path: '/teaching/:tab',
      name: 'TeachingOverview',
      component: TeachingOverview,
      props: function(route) {
        return { activeTab: route.params.tab }
      }
    },
    // 教学教研默认重定向到教学管理
    {
      path: '/teaching',
      redirect: '/teaching/management'
    },

    // ==================== 招生就业 ====================
    // 招生就业统一页面，通过tab参数切换不同内容
    {
      path: '/admission/:tab',
      name: 'AdmissionOverview',
      component: AdmissionOverview,
      props: function(route) {
        return { activeTab: route.params.tab }
      }
    },
    // 招生就业默认重定向到招生简章
    {
      path: '/admission',
      redirect: '/admission/guide'
    },

    // ==================== 合作交流 ====================
    // 合作交流统一页面，通过tab参数切换不同内容
    {
      path: '/cooperation/:tab',
      name: 'CooperationOverview',
      component: CooperationOverview,
      props: function(route) {
        return { activeTab: route.params.tab }
      }
    },
    // 合作交流默认重定向到合作交流概述
    {
      path: '/cooperation',
      redirect: '/cooperation/overview'
    },

    // ==================== 新闻模块 ====================
    {
      path: '/news/newsList',               // 新闻列表
      name: 'NewsList',
      component: NewsList
    },
    {
      path: '/news/newsDetails',            // 新闻详情
      name: 'NewsDetails',
      component: NewsDetails
    },

    // ==================== 公告模块 ====================
    {
      path: '/notice/noticeDetails',        // 公告详情
      name: 'NoticeDetails',
      component: NoticeDetails
    }
  ]
})

// 全局导航守卫：处理旧版HTML页面重定向
// 当URL包含 .html 文件时，根据文件名进行重定向
router.beforeEach((to, from, next) => {
  // 获取当前URL路径（包含hash模式下的完整路径）
  const currentPath = window.location.pathname;
  
  // 定义旧页面到新路由的映射关系
  const redirectMap = {
    '/xxgk.html': '/school/intro',
    '/zysz.html': '/majors/mechatronics',
    '/zsyjy.html': '/admission/guide',
    '/lxwm.html': '/cooperation/reservation'
  };
  
  // 检查是否需要重定向
  if (redirectMap[currentPath]) {
    // 使用replace避免在history中留下记录
    window.location.replace(redirectMap[currentPath]);
    return;
  }
  
  next();
})

export default router



// WEBPACK FOOTER //
// ./src/router/index.js